import React, { useCallback, useEffect, useState } from 'react';
import { SA_CITIES, toDelivery, validateAddress } from '../../lib/saAddress';
import { deleteAddress, listAddresses, saveAddress, setDefaultAddress } from '../../lib/marketAddresses';
import MapPicker from './MapPicker';

// Saudi delivery addresses: the form (AddressForm) and the customer's saved
// addresses (AddressBook) — used by checkout and by /market/addresses.
// Errors show only for a field the customer has left, or after they try to
// continue. The Short Address is required (the Saudi route's rule); its
// FORMAT is checked, and the page says so — it is not a verification.

export const ADDR_T = {
  ar: {
    name: 'اسم المستلم', phone: 'رقم الجوال', shortAddress: 'العنوان الوطني المختصر', city: 'المدينة', district: 'الحي', street: 'الشارع',
    buildingNo: 'رقم المبنى', postalCode: 'الرمز البريدي', additionalNo: 'الرقم الإضافي', unit: 'رقم الشقة أو الوحدة', details: 'تفاصيل إضافية',
    instructions: 'تعليمات التوصيل', label: 'اسم العنوان (مثل: المستودع)', more: 'تفاصيل إضافية (اختيارية)', less: 'إخفاء التفاصيل الإضافية',
    shortHint: '4 أحرف ثم 4 أرقام، مثل RAGI2929 — تجده في تطبيق «سبل» أو منصة العنوان الوطني. نتحقق من الصيغة فقط.',
    phoneHint: 'جوال سعودي يبدأ بـ 5', optional: 'اختياري',
    err: { required: 'هذا الحقل مطلوب.', format: { phone: 'رقم جوال سعودي من 9 أرقام يبدأ بـ 5.', shortAddress: 'الصيغة: 4 أحرف ثم 4 أرقام، مثل RAGI2929.',
      buildingNo: 'رقم المبنى 4 أرقام.', postalCode: 'الرمز البريدي 5 أرقام.', additionalNo: 'الرقم الإضافي 4 أرقام.' }, outside: 'الموقع المحدد خارج المملكة.' },
    saved: 'عناويني المحفوظة', addNew: 'إضافة عنوان جديد', edit: 'تعديل', remove: 'حذف', makeDefault: 'اجعله الافتراضي', isDefault: 'الافتراضي',
    save: 'حفظ العنوان', cancel: 'إلغاء', saveToAccount: 'احفظه في عناويني', liveNote: 'سيُستخدم هذا العنوان لهذا الطلب.', confirmDelete: 'حذف هذا العنوان؟ الطلبات السابقة لا تتأثر.',
    failed: 'تعذّر الحفظ، حاول مرة أخرى.', none: 'لا توجد عناوين محفوظة بعد.',
  },
  en: {
    name: 'Recipient name', phone: 'Mobile number', shortAddress: 'National Short Address', city: 'City', district: 'District', street: 'Street',
    buildingNo: 'Building number', postalCode: 'Postal code', additionalNo: 'Additional number', unit: 'Apartment / unit', details: 'More details',
    instructions: 'Delivery instructions', label: 'Address name (e.g. Warehouse)', more: 'More details (optional)', less: 'Hide the extra details',
    shortHint: '4 letters then 4 digits, e.g. RAGI2929 — shown in the SPL app or on the National Address portal. We check the format only.',
    phoneHint: 'Saudi mobile starting with 5', optional: 'optional',
    err: { required: 'This field is required.', format: { phone: 'A Saudi mobile: 9 digits starting with 5.', shortAddress: 'Format: 4 letters then 4 digits, e.g. RAGI2929.',
      buildingNo: 'Building number: 4 digits.', postalCode: 'Postal code: 5 digits.', additionalNo: 'Additional number: 4 digits.' }, outside: 'The selected point is outside Saudi Arabia.' },
    saved: 'My saved addresses', addNew: 'Add a new address', edit: 'Edit', remove: 'Delete', makeDefault: 'Make default', isDefault: 'Default',
    save: 'Save address', cancel: 'Cancel', saveToAccount: 'Save to my addresses', liveNote: 'This address will be used for this order.', confirmDelete: 'Delete this address? Past orders are not affected.',
    failed: 'Could not save — please try again.', none: 'No saved addresses yet.',
  },
};
ADDR_T.zh = ADDR_T.en;

const errText = (c, k, code) => (code === 'format' ? c.err.format[k] || c.err.required : code === 'outside' ? c.err.outside : c.err.required);

/** The address form. value/onChange: the camelCase address; showErrors: the customer tried to continue. */
export function AddressForm({ value, onChange, lang = 'ar', showErrors = false, font }) {
  const c = ADDR_T[lang] || ADDR_T.ar;
  const [touched, setTouched] = useState({});
  const [more, setMore] = useState(() => !!(value.postalCode || value.additionalNo || value.unit || value.details || value.instructions));
  const errs = validateAddress(value);
  const set = (k, v) => onChange({ ...value, [k]: v });
  const show = (k) => (touched[k] || showErrors) && errs[k];
  const input = (k, props = {}) => (
    <label className={`mk-af${props.wide ? ' wide' : ''}`} key={k}>
      <span>{c[k]}{props.optional && <em> · {c.optional}</em>}</span>
      <input data-field={k} value={value[k] ?? ''} dir={props.ltr ? 'ltr' : undefined} inputMode={props.inputMode} autoComplete={props.autoComplete}
        list={props.list} maxLength={props.maxLength} placeholder={props.placeholder} aria-invalid={!!show(k)}
        onChange={(e) => set(k, props.upper ? e.target.value.toUpperCase() : e.target.value)} onBlur={() => setTouched((t) => ({ ...t, [k]: true }))}
        style={{ fontFamily: font, textAlign: props.ltr && lang === 'ar' ? 'right' : undefined }} />
      {show(k) ? <small className="mk-af-err" data-field-error={k}>{errText(c, k, errs[k])}</small> : props.hint && <small>{props.hint}</small>}
    </label>
  );
  return (
    <div className="mk-address-form" data-address-form="1" data-clarity-mask="True">
      {input('name', { autoComplete: 'name' })}
      {input('phone', { ltr: true, inputMode: 'tel', autoComplete: 'tel', placeholder: '05XXXXXXXX', hint: c.phoneHint })}
      <div className="mk-af wide"><MapPicker lang={lang} value={value} onPick={(p) => onChange({
        ...value, lat: p.lat, lng: p.lng,
        // suggestions fill EMPTY fields only — never overwrite what the customer typed
        ...Object.fromEntries(['city', 'district', 'street', 'postalCode', 'buildingNo'].filter((k) => p[k] && !String(value[k] || '').trim()).map((k) => [k, p[k]])),
      })} />{errs.location && (touched.lat || showErrors) && <small className="mk-af-err">{c.err.outside}</small>}</div>
      {input('shortAddress', { ltr: true, upper: true, maxLength: 9, placeholder: 'RAGI2929', hint: c.shortHint, wide: true })}
      {input('city', { list: 'mk-sa-cities', autoComplete: 'address-level2' })}
      <datalist id="mk-sa-cities">{SA_CITIES.map((x) => <option key={x.en} value={lang === 'ar' ? x.ar : x.en} />)}</datalist>
      {input('district')}
      {input('street', { autoComplete: 'address-line1' })}
      {input('buildingNo', { ltr: true, inputMode: 'numeric', maxLength: 4 })}
      <button type="button" className="mk-af-more" data-address-more="1" aria-expanded={more} onClick={() => setMore((x) => !x)}>{more ? c.less : c.more}</button>
      {more && (
        <>
          {input('postalCode', { ltr: true, inputMode: 'numeric', maxLength: 5, optional: true, autoComplete: 'postal-code' })}
          {input('additionalNo', { ltr: true, inputMode: 'numeric', maxLength: 4, optional: true })}
          {input('unit', { optional: true })}
          {input('label', { optional: true })}
          {input('details', { optional: true, wide: true })}
          {input('instructions', { optional: true, wide: true })}
        </>
      )}
    </div>
  );
}

/** One line a person can read back: «Name · +9665… · RAGI2929 · 8228 Street, District, City». */
export function AddressSummary({ a, lang }) {
  const d = toDelivery(a);
  return (
    <span className="mk-addr-sum" data-clarity-mask="True">
      <b>{a.label ? `${a.label} — ` : ''}{d.name}</b>
      <span dir="ltr">{d.phone}</span>
      <span><span dir="ltr">{d.shortAddress}</span> · {d.address}, {d.district}, {d.city}{d.postalCode ? ` ${d.postalCode}` : ''}</span>
    </span>
  );
}

const EMPTY = { name: '', phone: '', shortAddress: '', city: '', district: '', street: '', buildingNo: '' };

/**
 * Saved addresses + a new one, without leaving the page.
 * onSelect(delivery | null): the chosen, valid address as the order's snapshot.
 * mode 'checkout' (choose one) or 'manage' (/market/addresses).
 * Guest checkout (2026-10-21): `draft` prefills a new address (what a guest
 * typed before signing in), `onDraftChange` reports every edit of a NEW
 * address so the page can keep it across sign-in, and `preferId` selects a
 * just-saved address once the list loads.
 */
export default function AddressBook({ user, lang = 'ar', onSelect, mode = 'checkout', font, showErrors = false, draft = null, onDraftChange, preferId = null }) {
  const c = ADDR_T[lang] || ADDR_T.ar;
  const [rows, setRows] = useState(null);
  const [chosen, setChosen] = useState(null);
  const [editing, setEditing] = useState(null);   // null | address being added / edited
  const [tried, setTried] = useState(false);
  const [err, setErr] = useState('');
  const load = useCallback(async () => {
    if (!user) { setRows([]); setEditing({ ...EMPTY, ...(draft || {}), id: undefined }); return; }
    const r = await listAddresses();
    setRows(r.rows);
    // An address typed before signing in stays the one in use (its total was
    // calculated for it), even when the account already has saved addresses.
    const typed = draft && Object.values({ ...EMPTY, ...draft }).some((v) => String(v ?? '').trim());
    if (!r.rows.length || (typed && !preferId)) setEditing({ ...EMPTY, ...(draft || {}), id: undefined });
    setChosen((cur) => (r.rows.some((x) => x.id === cur) ? cur
      : r.rows.some((x) => x.id === preferId) ? preferId
        : r.rows.find((x) => x.isDefault)?.id ?? r.rows[0]?.id ?? null));
  }, [user, preferId]); // eslint-disable-line react-hooks/exhaustive-deps
  // A new address being typed is reported as typed (the page keeps it).
  useEffect(() => { if (onDraftChange && editing && !editing.id) onDraftChange(editing); }, [editing]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  // The address the order uses: a NEW address being typed at checkout counts as
  // soon as it is valid (saving it to the account is a separate, optional
  // button); an existing one being edited counts only once saved; otherwise
  // the chosen saved address.
  const liveNew = mode === 'checkout' && editing && !editing.id;
  const liveValid = liveNew && !Object.keys(validateAddress(editing)).length;
  useEffect(() => {
    if (!onSelect) return;
    if (editing) { onSelect(liveValid ? toDelivery(editing) : null); return; }
    const a = rows?.find((x) => x.id === chosen);
    onSelect(a && !Object.keys(validateAddress(a)).length ? toDelivery(a) : null);
  }, [rows, chosen, editing]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    setTried(true); setErr('');
    if (Object.keys(validateAddress(editing)).length) return;
    if (!user) return;
    const r = await saveAddress(editing, user.id);
    if (r.error) { setErr(c.failed); console.warn('[AddressBook] save failed:', r.error); return; }
    setEditing(null); setTried(false);
    await load();
    setChosen(r.row.id);
  };
  const busyForm = editing && (
    <div className="mk-addr-edit" data-address-editing={editing.id || 'new'}>
      <AddressForm value={editing} onChange={setEditing} lang={lang} showErrors={tried || showErrors} font={font} />
      {liveValid && <p className="mk-count" data-address-live="1">{c.liveNote}</p>}
      {err && <p className="mk-af-err" role="alert">{err}</p>}
      <div className="mk-addr-actions">
        {user && <button type="button" className={`mk-btn${liveNew ? ' ghost' : ''}`} data-address-submit="1" onClick={submit}>{liveNew ? c.saveToAccount : c.save}</button>}
        {rows?.length > 0 && <button type="button" className="mk-btn ghost" onClick={() => { setEditing(null); setTried(false); }}>{c.cancel}</button>}
      </div>
    </div>
  );

  if (rows == null) return <div className="mk-skel" style={{ height: 120 }} />;
  return (
    <div className="mk-addr-book" data-address-book={rows.length} data-clarity-mask="True">
      {rows.length > 0 && (
        <div role="radiogroup" aria-label={c.saved} className="mk-addr-list">
          {rows.map((a) => (
            <div key={a.id} className={`mk-addr${chosen === a.id ? ' on' : ''}`} data-address={a.id}>
              {mode === 'checkout'
                ? <label className="mk-addr-pick"><input type="radio" name="mk-addr" checked={chosen === a.id} onChange={() => { setChosen(a.id); setEditing(null); }} data-address-pick={a.id} /><AddressSummary a={a} lang={lang} /></label>
                : <AddressSummary a={a} lang={lang} />}
              <div className="mk-addr-tools">
                {a.isDefault ? <span className="mk-addr-default" data-address-default="1">{c.isDefault}</span>
                  : <button type="button" onClick={async () => { await setDefaultAddress(a.id); load(); }} data-address-make-default={a.id}>{c.makeDefault}</button>}
                <button type="button" onClick={() => { setEditing({ ...a }); setTried(false); }} data-address-edit={a.id}>{c.edit}</button>
                <button type="button" onClick={async () => { if (window.confirm(c.confirmDelete)) { await deleteAddress(a.id); load(); } }} data-address-delete={a.id}>{c.remove}</button>
              </div>
            </div>
          ))}
          {rows.length === 0 && <p className="mk-count">{c.none}</p>}
        </div>
      )}
      {editing ? busyForm : <button type="button" className="mk-btn ghost" data-address-add="1" onClick={() => { setEditing({ ...EMPTY }); setTried(false); }}>{c.addNew}</button>}
    </div>
  );
}
